// Authentication API Logic
// Integrated with backend API at /api/v1/auth

import { emitUnauthorizedLogout } from './authEvents';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  mfa_enabled?: boolean;
  last_login_at?: string;
  created_at: string;
  updated_at: string;
}

// Helper to get full name from user
export const getUserFullName = (user: User): string => {
  return `${user.first_name} ${user.last_name}`.trim();
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data?: any;
  error?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone?: string;
}

export interface ResetPasswordData {
  newPassword: string;
  confirmNewPassword: string;
}

// Token management
const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};

const getRefreshToken = (): string | null => {
  return localStorage.getItem('refresh_token');
};

const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem('access_token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
};

const clearTokens = (): void => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

// Get current user from localStorage
const getCurrentUser = (): User | null => {
  const user = localStorage.getItem('casino_builder_current_user');
  return user ? JSON.parse(user) : null;
};

// Set current user in localStorage
const setCurrentUser = (user: User | null): void => {
  if (user) {
    localStorage.setItem('casino_builder_current_user', JSON.stringify(user));
  } else {
    localStorage.removeItem('casino_builder_current_user');
  }
};


// Validate email format
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate password strength
const isValidPassword = (password: string): boolean => {
  // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
};

// Generate unique ID
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

/**
 * Register a new user
 */
export const register = async (data: RegisterData): Promise<AuthResponse> => {
  try {
    // Validation
    if (!data.firstName.trim()) {
      return { success: false, message: 'First name is required' };
    }

    if (!data.lastName.trim()) {
      return { success: false, message: 'Last name is required' };
    }

    if (!data.email.trim()) {
      return { success: false, message: 'Email is required' };
    }

    if (!isValidEmail(data.email)) {
      return { success: false, message: 'Please enter a valid email address' };
    }

    if (!data.password) {
      return { success: false, message: 'Password is required' };
    }

    if (!isValidPassword(data.password)) {
      return { 
        success: false, 
        message: 'Password must be at least 8 characters with uppercase, lowercase, and number' 
      };
    }

    if (data.password !== data.confirmPassword) {
      return { success: false, message: 'Passwords do not match' };
    }

    // Validate phone if provided (min 10, max 15 characters as per backend)
    if (data.phone && data.phone.trim()) {
      const phone = data.phone.trim();
      if (phone.length < 10 || phone.length > 15) {
        return { success: false, message: 'Phone number must be between 10 and 15 characters' };
      }
    }

    // Prepare request body
    const requestBody: any = {
      email: data.email.toLowerCase().trim(),
      password: data.password,
      first_name: data.firstName.trim(),
      last_name: data.lastName.trim(),
    };

    // Add phone if provided
    if (data.phone && data.phone.trim()) {
      requestBody.phone = data.phone.trim();
    }

    // Call backend API
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const responseData = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: responseData.error || 'Registration failed. Please try again.',
        error: responseData.error || 'Unknown error',
      };
    }

    // Store tokens and user
    if (responseData.access_token && responseData.refresh_token) {
      setTokens(responseData.access_token, responseData.refresh_token);
    }

    if (responseData.user) {
      setCurrentUser(responseData.user);
    }

    return {
      success: true,
      message: 'Registration successful! Welcome to CSZGLOBAL Casino Builder.',
      data: {
        user: responseData.user,
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token,
      },
    };
  } catch (error) {
    return {
      success: false,
      message: 'Registration failed. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Login user
 */
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  try {
    // Validation
    if (!credentials.email.trim()) {
      return { success: false, message: 'Email is required' };
    }

    if (!credentials.password) {
      return { success: false, message: 'Password is required' };
    }

    if (!isValidEmail(credentials.email)) {
      return { success: false, message: 'Please enter a valid email address' };
    }

    // Call backend API
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: credentials.email.toLowerCase().trim(),
        password: credentials.password,
      }),
    });

    const responseData = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: responseData.error || 'Invalid email or password',
        error: responseData.error || 'Login failed',
      };
    }

    // Store tokens and user
    if (responseData.access_token && responseData.refresh_token) {
      setTokens(responseData.access_token, responseData.refresh_token);
    }

    if (responseData.user) {
      setCurrentUser(responseData.user);
    }

    return {
      success: true,
      message: 'Login successful! Welcome back.',
      data: {
        user: responseData.user,
        access_token: responseData.access_token,
        refresh_token: responseData.refresh_token,
      },
    };
  } catch (error) {
    return {
      success: false,
      message: 'Login failed. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Logout user
 */
export const logout = async (): Promise<AuthResponse> => {
  try {
    const accessToken = getAccessToken();

    // Call backend API if token exists
    if (accessToken) {
      try {
        await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });
      } catch (error) {
        // Continue with local logout even if API call fails
        console.error('Logout API call failed:', error);
      }
    }

    // Clear local storage
    setCurrentUser(null);
    clearTokens();

    return {
      success: true,
      message: 'You have been logged out successfully.',
    };
  } catch (error) {
    // Even if there's an error, clear local storage
    setCurrentUser(null);
    clearTokens();

    return {
      success: false,
      message: 'Logout failed. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Forgot password - send OTP
 * Note: This is a mock implementation. Backend API integration pending.
 */
export const forgotPassword = async (email: string): Promise<AuthResponse> => {
  try {
    // Validation
    if (!email.trim()) {
      return { success: false, message: 'Email is required' };
    }

    if (!isValidEmail(email)) {
      return { success: false, message: 'Please enter a valid email address' };
    }

    // Store OTP in localStorage (in real app, this would be sent via email via backend API)
    localStorage.setItem('casino_builder_reset_otp', '123456');
    localStorage.setItem('casino_builder_reset_email', email.toLowerCase());

    return { 
      success: true, 
      message: 'An OTP has been sent to your registered email address.'
    };

  } catch (error) {
    return { 
      success: false, 
      message: 'Failed to send OTP. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Verify OTP
 * Note: This is a mock implementation. Backend API integration pending.
 */
export const verifyOtp = async (otp: string): Promise<AuthResponse> => {
  try {
    // Validation
    if (!otp.trim()) {
      return { success: false, message: 'OTP is required' };
    }

    if (otp.length !== 6) {
      return { success: false, message: 'OTP must be 6 digits' };
    }

    // Check stored OTP
    const storedOtp = localStorage.getItem('casino_builder_reset_otp');
    const resetEmail = localStorage.getItem('casino_builder_reset_email');
    
    if (!storedOtp || !resetEmail) {
      return { success: false, message: 'No OTP verification in progress' };
    }

    if (otp !== storedOtp) {
      return { success: false, message: 'Invalid OTP. Please try again.' };
    }

    return { 
      success: true, 
      message: 'OTP verified successfully.',
      data: { email: resetEmail }
    };

  } catch (error) {
    return { 
      success: false, 
      message: 'OTP verification failed. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Reset password
 * Note: This is a mock implementation. Backend API integration pending.
 */
export const resetPassword = async (data: ResetPasswordData): Promise<AuthResponse> => {
  try {
    // Validation
    if (!data.newPassword) {
      return { success: false, message: 'New password is required' };
    }

    if (!isValidPassword(data.newPassword)) {
      return { 
        success: false, 
        message: 'Password must be at least 8 characters with uppercase, lowercase, and number' 
      };
    }

    if (data.newPassword !== data.confirmNewPassword) {
      return { success: false, message: 'Passwords do not match' };
    }

    // Check if OTP was verified
    const resetEmail = localStorage.getItem('casino_builder_reset_email');
    if (!resetEmail) {
      return { success: false, message: 'No password reset in progress' };
    }

    // In a real implementation, this would call the backend API to reset the password
    // For now, we'll just clear the reset data and return success
    // The actual password reset would be handled by the backend API

    // Clear reset data
    localStorage.removeItem('casino_builder_reset_otp');
    localStorage.removeItem('casino_builder_reset_email');

    return { 
      success: true, 
      message: 'Password reset successful! You can now login with your new password.'
    };

  } catch (error) {
    return { 
      success: false, 
      message: 'Password reset failed. Please try again.',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Get user profile
 */
export const getProfile = async (): Promise<AuthResponse> => {
  try {
    const accessToken = getAccessToken();

    if (!accessToken) {
      return { success: false, message: 'No access token found. Please login again.' };
    }

    // Call backend API
    const response = await fetch(`${API_BASE_URL}/api/v1/user/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    const responseData = await response.json();

    if (!response.ok) {
      // If unauthorized, clear tokens and user
      if (response.status === 401) {
        setCurrentUser(null);
        clearTokens();
        emitUnauthorizedLogout();
      }
      return {
        success: false,
        message: responseData.error || 'Failed to retrieve profile.',
        error: responseData.error || 'Unknown error',
      };
    }

    // Update stored user data
    if (responseData) {
      setCurrentUser(responseData);
    }

    return {
      success: true,
      message: 'Profile retrieved successfully.',
      data: { user: responseData },
    };
  } catch (error) {
    return {
      success: false,
      message: 'Failed to retrieve profile.',
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const user = getCurrentUser();
  const token = getAccessToken();
  return user !== null && token !== null;
};

/**
 * Get current user
 */
export const getCurrentUserData = (): User | null => {
  return getCurrentUser();
};
